import {
  AfterViewInit,
  Component,
  ComponentRef, ElementRef,
  HostBinding,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import interact from 'interactjs';
import SelectableComponent from '../../interfaces/selectableComponent';
import { CommunicationService } from '../../services/communication.service';
import { DynamicElementService } from '../../services/dynamicElement.service';
import { WidgetType } from '../../util/WidgetType';
import { SpacerHelper } from '../informationview/spacerHelper';
import { DraggedWidgetHolder } from './DraggedWidgetHolder';
import { StateTransitionHelper } from './StateTransitionHelper';
import { WidgetWrapperState } from './widgetWrapperState';

interface InjectedData {
  widgetType: WidgetType;
  widgetName: string;
}

@Component({
  selector: 'app-widget-wrapper',
  templateUrl: './widget-wrapper.component.html',
  styleUrls: ['./widget-wrapper.component.scss'],
  })
export class WidgetWrapperComponent implements OnInit, OnDestroy, AfterViewInit, SelectableComponent {
  @HostBinding('style.z-index')
  public zIndex;
  @ViewChild('widgetInsertionPoint', { read: ViewContainerRef, static: true }) widgetInsertionPoint: ViewContainerRef;
  @ViewChild('widgetWrapper') widgetWrapper: ElementRef;
  public widgetType: WidgetType;

  protected widgetContainerId = 'widgetcontainer';
  public widgetContainer: HTMLElement;
  public wrapperElement: HTMLElement;
  public widgetName: string;
  public identifier: string;
  public wrapperComponentRef: ComponentRef<WidgetWrapperComponent>;
  // TODO can we use type IWidget here?
  public widgetComponentRef: ComponentRef<any>;
  public dragBlocked: boolean;
  public readonly placeholderElement: HTMLElement;
  public state: WidgetWrapperState;
  protected transitionHelper = StateTransitionHelper;

  constructor(
    protected dynamicElementService: DynamicElementService,
    protected communicationService: CommunicationService,
    @Inject('widgetType') protected injectedData: InjectedData,
  ) {
    this.state = WidgetWrapperState.DOCKED_RESTING;
    this.widgetType = injectedData.widgetType;
    this.widgetName = injectedData.widgetName;
    this.placeholderElement = document.createElement('div');
    this.placeholderElement.classList.add('widget', 'widget--placeholder');
  }

  ngOnInit(): void {
    this.widgetComponentRef = this.widgetInsertionPoint.createComponent(this.widgetType.type);
    this.identifier = this.widgetName + '-widget';
  }

  ngAfterViewInit() {
    this.widgetContainer = document.getElementById(this.widgetContainerId);
    this.wrapperElement = this.widgetWrapper.nativeElement;
    this.wrapperElement.setAttribute('data-widget-name', this.widgetName);
    this.widgetContainer.insertBefore(SpacerHelper.getNewSpacerElement(), this.wrapperElement.parentElement);
    this.setupInteraction();
  }

  ngOnDestroy(): void {
    interact(`#${this.identifier}`).unset();
    interact(`#${this.identifier} > div.widget__inner > div.widget__header`).unset();
  }

  setDragBlocked(dragBlocked: boolean) {
    this.dragBlocked = dragBlocked;
  }

  /**
   * Move this Widgewrapper to foreground on mousedown if it is in dynamicElementContainer
   */
  public onMouseDown() {
    if (!this.isInDock()) {
      this.dynamicElementService.select(this.wrapperComponentRef);
    }
  }

  protected setupInteraction() {
    let documentClientWidth;
    let documentClientHeight;

    // Drag interaction (only the widget-header is draggable)
    interact(`#${this.identifier} > div.widget__inner > div.widget__header`)
      .draggable({
        onstart: event => this.handleDragStart(event),
        onmove: event => this.handleDragMove(event),
        onend: event => this.handleDragEnd(event),
      });

    // Resize interaction
    interact(`#${this.identifier}`)
      .resizable({
        edges: {
          top: false,
          left: false,
          bottom: '.widget__resizer',
          right: '.widget__resizer'
        },
        onstart: event => {
          this.wrapperElement.style.transitionDuration = '0ms';
          documentClientWidth = document.documentElement.clientWidth;
          documentClientHeight = document.documentElement.clientHeight;
        },
        onmove: event => {
          const percentViewportWidth = event.rect.width / documentClientWidth * 100;
          const percentViewportHeight = event.rect.height / documentClientHeight * 100;
          event.target.style.width = percentViewportWidth + 'vw';
          event.target.style.height = percentViewportHeight + 'vh';
        },
        onend: event => {
          this.wrapperElement.style.transitionDuration = null;
          documentClientWidth = null;
          documentClientHeight = null;
        }
      });


    // scroll widget-content
    interact(`#${this.identifier}`)
      .gesturable({
        onmove: event => {
          document.querySelector(`#${this.identifier} .widget__inner`).scrollTop -= event.delta.y;
        }
      });
  }

  protected handleDragStart(event: any) {
    DraggedWidgetHolder.draggedWidget = this;
    const newState = (this.isDocked()) ? WidgetWrapperState.DOCKED_DRAGGING : WidgetWrapperState.UNDOCKED_DRAGGING;
    this.setState(newState);
  }

  protected handleDragMove(event: any) {
    const top = event.rect.top;
    const left = event.rect.left;

    this.wrapperElement.style.top = `${top}px`;
    this.wrapperElement.style.left = `${left}px`;
  }

  protected handleDragEnd(event: any) {
    const newState = (this.isDocked()) ? WidgetWrapperState.DOCKED_RESTING : WidgetWrapperState.UNDOCKED_RESTING;
    this.setState(newState);

    this.wrapperElement.style.left = event.rect.left;
    this.wrapperElement.style.top = event.rect.top;
  }

  public setState(newState: WidgetWrapperState, mousePositionX?: number): void {
    const oldState = this.state;
    this.state = newState;
    this.transitionHelper.transition(this, oldState, newState, this.dynamicElementService, this.communicationService, mousePositionX);
  }

  /**
   * Close-Button clicked in Wrapper-GUI. We send an event to the informationview telling it to
   * dock this wrapper.
   */
  public onDockButtonClicked() {
    this.setState(WidgetWrapperState.DOCKED_RESTING);
  }

  public getState() {
    return this.state;
  }

  public isDocked(): boolean {
    return (this.state === WidgetWrapperState.DOCKED_DRAGGING || this.state === WidgetWrapperState.DOCKED_RESTING);
  }

  public isInDock(): boolean {
    return this.widgetContainer.contains(this.wrapperElement);
  }
}
